import DubbedMobile from "@/modules/dubbed/DubbedMobile";
import Layout from "@/modules/layout/Layout";
import codeToIdx from "@/modules/utils/codeToIdx";
import getLocales from "@/modules/utils/getLocales";
import { Global } from "@emotion/react";
import { GetServerSidePropsContext } from "next";
import { useTranslation } from "next-i18next";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import parser from "ua-parser-js";
import ENV from "@/scripts/client/ENV_PUBLIC";

const DubbedMobilePage = ({
  firstDubbed,
  firstScript,
  dubbedList,
  locale,
  isIos,
}: any) => {
  const { t } = useTranslation();
  const urlContent = `${locale !== "en" ? `/${locale}` : ""}/dubbed/${
    firstDubbed.code
  }?dl=${locale}`;
  const titleContent = firstDubbed.together_flag
    ? t("share_dubbed_together", {
        name1: firstDubbed.users[0]?.name ?? "",
        name2: firstDubbed.users[1]?.name ?? "",
      })
    : t("share_dubbed_single", {
        name: firstDubbed.users[0]?.name ?? "",
      });
  const descriptionContent = t("share_thumb_body");
  const image_locale = locale === "ko" ? "ko" : "en";
  const imageContent = `/img/snsshare2dubdub_${image_locale}.png`;

  return (
    <Layout
      title="2DUB - DUBBED"
      locale={locale}
      meta={
        <>
          <meta
            name="viewport"
            content="width=device-width,minimum-scale=1.0"
          />
          <meta property="og:url" content={urlContent} />
          <meta property="og:title" content={titleContent} />
          <meta property="og:description" content={descriptionContent} />
          <meta property="og:image" content={imageContent} />
          <meta property="og:type" content="website" />
          <meta name="theme-color" content="#000000" />
        </>
      }
    >
      <Global
        styles={{
          html: { height: "100%" },
          body: { height: "100%", background: "black" },
          "#__next": { height: "100%" },
        }}
      />
      <DubbedMobile
        firstDubbed={firstDubbed}
        firstScript={firstScript}
        dubbedList={dubbedList}
        locale={locale}
        isIos={isIos}
      />
    </Layout>
  );
};

const getDubbed = async (idx: string, locale: any) => {
  try {
    const response = await fetch(
      `${ENV.NEXT_PUBLIC_2DUB_URL}/api/video/dubbed/${idx}?dl=${locale}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    const result = await response.json();

    return result;
  } catch (error) {
    return error;
  }
};

const getScript = async (idx: string, locale: any) => {
  try {
    const response = await fetch(
      `${ENV.NEXT_PUBLIC_2DUB_URL}/api/v2/dubbed/${idx}/script?dl=${locale}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    const result = await response.json();

    return result;
  } catch (error) {
    return error;
  }
};

const getRecommendList = async () => {
  try {
    const response = await fetch(
      `${ENV.NEXT_PUBLIC_2DUB_URL}/api/v2/dubbing/rank?type=L&start=0&limit=12`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    const result = await response.json();
    if (result.code === 200) {
      return result.data.map((el) => el.idx);
    }
    return [];
  } catch (error) {
    return error;
  }
};

export async function getServerSideProps(context: GetServerSidePropsContext) {
  try {
    // 모듈화 하기
    const { query } = context;
    const locale = query.dl ?? getLocales(context);
    const { code = "", list = [] } = query;
    const idx = codeToIdx(code);

    const { ua } = parser(context.req.headers["user-agent"]);
    const isIos = ua.match(/iPhone|iPad|iPod/gi);

    if (idx === null) {
      return {
        redirect: {
          destination: `/`,
          permanent: false,
        },
      };
    }
    const fixList = typeof list === "string" ? list.split(",") : list;

    const dubbedList =
      fixList.length > 0
        ? fixList.map((el) => codeToIdx(el))
        : await getRecommendList();

    const [firstDubbed, firstScript] = await Promise.all([
      getDubbed(idx, locale),
      getScript(idx, locale),
    ]);

    if (firstDubbed.code !== 200 || firstScript.code !== 200) {
      return {
        redirect: {
          destination: `/`,
          permanent: false,
        },
      };
    }

    return {
      props: {
        locale,
        firstDubbed: firstDubbed.data,
        firstScript: firstScript.data,
        dubbedList,
        isIos,
        ...(await serverSideTranslations(locale)),
      },
    };
  } catch (error) {
    return error;
  }
}

export default DubbedMobilePage;
